import { API_URL } from './client';

/**
 * Notifiche in tempo reale dell'area admin (Parte B del piano).
 *
 * Il flag `VITE_WEBSOCKET_ENABLED` è **incorporato nel bundle** da Vite al momento
 * della build: per attivarlo serve ricostruire il frontend. Con il flag spento
 * (default) non viene aperto alcun socket e l'area admin continua a usare il
 * polling del REST (fallback identico al comportamento precedente).
 */
export const WEBSOCKET_ENABLED = import.meta.env.VITE_WEBSOCKET_ENABLED === 'true';

/** Messaggio inviato dal backend (`services/websocket.ts`): nessun dato personale. */
export interface AdminNotification {
    type: 'request:created';
    eventId: number;
    requestId: number;
}

/**
 * URL del socket, derivato dall'API: `https://` → `wss://`, `http://` → `ws://`,
 * più il path `/ws`. Non si usa `window.location`: in produzione l'API sta su un
 * dominio diverso da quello dell'app.
 */
export function websocketUrl(): string {
    const base = API_URL.replace(/\/+$/, '');
    const wsBase = base.replace(/^https:\/\//i, 'wss://').replace(/^http:\/\//i, 'ws://');
    return `${wsBase}/ws`;
}

/** Type guard minimo: `type` noto ed `eventId` numerico, tutto il resto ignorato. */
function isRequestCreated(value: unknown): value is AdminNotification {
    if (typeof value !== 'object' || value === null) return false;
    const message = value as { type?: unknown; eventId?: unknown };
    return message.type === 'request:created' && typeof message.eventId === 'number';
}

/**
 * Apre il socket delle notifiche admin e restituisce la funzione di cleanup (da
 * usare nel `return` dell'`useEffect`), che chiude il socket e rimuove i listener.
 *
 * Il cookie `ad_token` è `HttpOnly` e viene inviato dal browser nell'handshake
 * (stesso sito), quindi non c'è nessun token da passare: il server autentica e
 * rifiuta con 401/403 chi non è admin.
 *
 * Default: **nessuna riconnessione automatica**. Se il socket cade, il polling di
 * `RequestSection` continua a tenere aggiornata la lista (la notifica persa viene
 * recuperata dal REST). Un messaggio non valido viene ignorato senza lanciare.
 */
export function connectAdminNotifications(onRequestCreated: (eventId: number) => void): () => void {
    if (!WEBSOCKET_ENABLED) return () => undefined;

    let socket: WebSocket;
    try {
        socket = new WebSocket(websocketUrl());
    } catch {
        // Ambiente senza WebSocket (o URL non valido): si resta sul polling.
        return () => undefined;
    }

    const handleMessage = (event: MessageEvent) => {
        let message: unknown;
        try {
            message = JSON.parse(String(event.data));
        } catch {
            return;
        }

        if (isRequestCreated(message)) onRequestCreated(message.eventId);
    };

    socket.addEventListener('message', handleMessage);

    return () => {
        socket.removeEventListener('message', handleMessage);
        socket.close();
    };
}
